import React, { useContext, useEffect, useRef, useState } from 'react';
import mapApi from '../utils/api/map';
import _ from 'lodash';

type FilterOption = {
  value: string;
  label: string;
};

type FilterOptionsGroup = {
  [key: string]: FilterOption[];
};

type FilterSelections = {
  [key: string]: string | string[];
};

type Project = {
  id: string;
  lat: number;
  lng: number;
  projectName: string;
  [key: string]: any;
};

type MapsContextType = {
  activeFilter: string;
  setActiveFilter: React.Dispatch<React.SetStateAction<string>>;
  selectedOptions: FilterSelections;
  setSelectedOptions: React.Dispatch<React.SetStateAction<FilterSelections>>;
  searchRef: React.RefObject<HTMLInputElement> | null;
  mapData: Project[];
  projectFilterOptions: FilterOptionsGroup;
  inspectorFilterOptions: FilterOptionsGroup;
  refreshMapData: () => Promise<void>;
  resetData: () => void;
  handleSearchFunc: () => void;
};

const defaultInspectorFilterOptions: FilterOptionsGroup = {
  regions: [{ value: 'all', label: 'All Regions' }],
  regionalManagers: [],
  areaManagers: [],
  inspectors: [],
};

const defaultProjectFilterOptions: FilterOptionsGroup = {
  regions: [{ value: 'all', label: 'All Regions' }],
  divisions: [],
  clients: [],
  projectTypes: [],
  projectContacts: [],
};

const MapsContext = React.createContext<MapsContextType>({
  activeFilter: 'project',
  setActiveFilter: () => {},
  selectedOptions: {
    regions: 'all',
    divisions: [],
    clients: [],
    projectTypes: [],
    projectContacts: [],
    regionalManagers: [],
    areaManagers: [],
    inspectors: [],
  },
  setSelectedOptions: () => {},
  searchRef: null,
  mapData: [],
  projectFilterOptions: defaultProjectFilterOptions,
  inspectorFilterOptions: defaultInspectorFilterOptions,
  refreshMapData: async () => {},
  resetData: () => {},
  handleSearchFunc: () => {},
});

export const MapsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<FilterSelections>({
    regions: 'all',
    divisions: [],
    clients: [],
    projectTypes: [],
    projectContacts: [],
    regionalManagers: [],
    areaManagers: [],
    inspectors: [],
  });
  const searchRef = useRef<HTMLInputElement>(null);
  const [mapData, setMapData] = useState<Project[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('project');
  const [projectFilterOptions, setProjectFilterOptions] =
    useState<FilterOptionsGroup>(defaultProjectFilterOptions);
  const [inspectorFilterOptions, setInspectorFilterOptions] =
    useState<FilterOptionsGroup>(defaultInspectorFilterOptions);

  // Fetch initial map data on mount
  useEffect(() => {
    refreshMapData();
  }, []);

  // Fetch filter options once on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      const [projectRes, inspectorRes] = await Promise.all([
        activeFilter === 'project'
          ? mapApi.getDashboardMapsFilterDropdownProject(selectedOptions)
          : ({} as FilterOptionsGroup),
        activeFilter === 'inspector'
          ? mapApi.getDashboardMapsFilterDropdownInspector(selectedOptions)
          : ({} as FilterOptionsGroup),
      ]);

      setProjectFilterOptions(
        prependDefaultOption(projectRes, defaultProjectFilterOptions),
      );
      setInspectorFilterOptions(
        prependDefaultOption(inspectorRes, defaultInspectorFilterOptions),
      );
    };

    fetchFilterOptions();
  }, [selectedOptions, activeFilter]);

  // Helper function to prepend default options
  const prependDefaultOption = (
    fetched: FilterOptionsGroup,
    defaults: FilterOptionsGroup,
  ): FilterOptionsGroup => {
    const merged: FilterOptionsGroup = {};
    for (const key in defaults) {
      merged[key] = [
        ...(defaults[key] || []),
        ...(fetched[key] || []).filter((opt) => opt.value !== 'all'),
      ];
    }
    return merged;
  };

  // Fetch map data when filters change
  const refreshMapData = async () => {
    let params: any;
    if (activeFilter === 'project') {
      params = {
        search: searchRef.current?.value ?? '',
        regions: selectedOptions.regions,
        divisions: selectedOptions.divisions,
        clients: selectedOptions.clients,
        projectTypes: selectedOptions.projectTypes,
        projectContacts: selectedOptions.projectContacts,
      };
    } else if (activeFilter === 'inspector') {
      params = {
        search: searchRef.current?.value ?? '',
        regions: selectedOptions.regions,
        regionalManagers: selectedOptions.regionalManagers,
        areaManagers: selectedOptions.areaManagers,
        inspectors: selectedOptions.inspectors,
      };
    } else {
      throw new Error(`Unknown type: ${activeFilter}`);
    }
    const res = await mapApi.getProjectsMapsData(params);
    setMapData(res.data || []);
  };

  // Reset selected options and fetch initial map data
  const resetData = () => {
    setSelectedOptions({
      regions: 'all',
      divisions: [],
      clients: [],
      projectTypes: [],
      projectContacts: [],
      regionalManagers: [],
      areaManagers: [],
      inspectors: [],
    });
    refreshMapData();
  };

  // Debounced search function to refresh map data
  const handleSearchFunc = _.debounce(() => {
    refreshMapData();
  }, 1000);

  return (
    <MapsContext.Provider
      value={{
        activeFilter,
        setActiveFilter,
        selectedOptions,
        setSelectedOptions,
        searchRef,
        mapData,
        projectFilterOptions,
        inspectorFilterOptions,
        refreshMapData,
        resetData,
        handleSearchFunc,
      }}
    >
      {children}
    </MapsContext.Provider>
  );
};

export const useMapsContext = () => {
  const context = useContext(MapsContext);
  if (!context) {
    throw new Error('useMapsContext must be used within a MapsProvider');
  }
  return context;
};
